// Sourced from @sentry/node (a declared dependency) rather than @sentry/core,
// which is only present transitively. The types are identical — both SDKs
// re-export them from core — and these are erased at build time, so the
// server-side import carries no weight in browser bundles.
import type { ErrorEvent, EventHint } from "@sentry/node";

/**
 * Default PII scrubbing applied to every Sentry event across all apps.
 *
 * We process special-category (health/recovery) data, so error reports must not
 * carry member or facilitator personal data to a third-party processor. This
 * runs as the baseline `beforeSend` in `initSentry`; apps extend it via
 * `extraCookies` rather than replacing it.
 *
 * Deliberately NOT scrubbed: `event.user`, which `identifyUser` sets so Better
 * Stack can report affected-user counts. That is safe because `identifyUser`
 * attaches only an opaque account UUID — never email (GH #1085). If anything
 * ever puts personal data on `event.user` again, this exemption has to go with
 * it, because nothing downstream will catch it.
 */

/**
 * Cookie names carrying PII in every app. Session/auth cookies are included
 * because their values are bearer credentials — a leaked error report
 * containing one is an account-takeover vector, not just a privacy issue.
 */
const DEFAULT_PII_COOKIES = new Set([
  "member_id",
  "pending_email",
  "sb-access-token",
  "sb-refresh-token",
]);

/** Supabase issues project-scoped auth cookies as `sb-<ref>-auth-token[.n]`. */
const SUPABASE_AUTH_COOKIE = /^sb-.*-auth-token(\.\d+)?$/;

/**
 * Headers whose values are credentials or directly identify a person.
 * Sentry sends request headers by default.
 */
const PII_HEADERS = new Set([
  "authorization",
  "cookie",
  "proxy-authorization",
  "set-cookie",
  "x-forwarded-for",
  "x-organization-id",
  "x-real-ip",
]);

const FILTERED = "[Filtered]";

function isPiiCookie(name: string, extraCookies: Set<string>): boolean {
  return (
    DEFAULT_PII_COOKIES.has(name) ||
    extraCookies.has(name) ||
    SUPABASE_AUTH_COOKIE.test(name)
  );
}

function scrubCookies(
  cookies: Record<string, string>,
  extraCookies: Set<string>
): void {
  for (const name of Object.keys(cookies)) {
    if (isPiiCookie(name, extraCookies)) {
      cookies[name] = FILTERED;
    }
  }
}

function scrubHeaders(headers: Record<string, string>): void {
  for (const name of Object.keys(headers)) {
    if (PII_HEADERS.has(name.toLowerCase())) {
      headers[name] = FILTERED;
    }
  }
}

/**
 * Request bodies are dropped wholesale rather than key-filtered: they carry
 * orientation forms, CRM notes and member lookups, and an allowlist would fail
 * open every time a new field is added.
 */
function scrubRequest(
  request: NonNullable<ErrorEvent["request"]>,
  extraCookies: Set<string>
): void {
  if (request.data !== undefined) {
    request.data = FILTERED;
  }

  if (request.cookies && typeof request.cookies === "object") {
    scrubCookies(request.cookies as Record<string, string>, extraCookies);
  }

  if (request.headers && typeof request.headers === "object") {
    scrubHeaders(request.headers as Record<string, string>);
  }

  // The full URL carries the same emails and tokens as the query string
  // (reset/invite/lookup links), and Sentry populates it separately — scrubbing
  // only query_string leaves them in request.url.
  if (typeof request.url === "string") {
    request.url = scrubUrl(request.url) as string;
  }

  // Query strings routinely carry emails and tokens in reset/invite links.
  // Sentry sends this as a string, an object, or an array of pairs, so a
  // string-only guard silently passes the structured forms through.
  if (request.query_string != null && request.query_string !== "") {
    request.query_string = FILTERED;
  }
}

/**
 * Breadcrumbs record fetch/xhr traffic and console output, so they leak the
 * same data as the request itself one step removed.
 */
function scrubBreadcrumbs(event: ErrorEvent): void {
  if (!event.breadcrumbs) {
    return;
  }

  for (const crumb of event.breadcrumbs) {
    if (crumb.category === "console" && crumb.data) {
      crumb.data = { arguments: FILTERED };
    }

    if (
      (crumb.category === "fetch" || crumb.category === "xhr") &&
      crumb.data
    ) {
      const { method, status_code, url } = crumb.data;
      crumb.data = { method, status_code, url: scrubUrl(url) };
    }

    // Navigation crumbs record the to/from URLs, which carry the same tokens
    // and emails as request.url.
    if (crumb.category === "navigation" && crumb.data) {
      crumb.data = {
        from: scrubUrl(crumb.data.from),
        to: scrubUrl(crumb.data.to),
      };
    }
  }
}

/** Strip query params, keeping origin + path for debugging context. */
function scrubUrl(url: unknown): unknown {
  if (typeof url !== "string" || !url.includes("?")) {
    return url;
  }
  return `${url.split("?")[0]}?${FILTERED}`;
}

export interface ScrubOptions {
  /** App-specific cookie names to filter on top of the shared defaults. */
  extraCookies?: readonly string[];
}

/**
 * Build the `beforeSend` hook used by `initSentry`. Mutates and returns the
 * event; returning `null` would drop the error entirely, losing the signal we
 * rely on for breach detection.
 */
export function createBeforeSend(options: ScrubOptions = {}) {
  const extraCookies = new Set(options.extraCookies ?? []);

  return (event: ErrorEvent, _hint?: EventHint): ErrorEvent => {
    if (event.request) {
      scrubRequest(event.request, extraCookies);
    }

    scrubBreadcrumbs(event);

    return event;
  };
}
